import { z } from 'zod';
import { SocialProvider } from './social-auth.js';
/**
 * Closing your own account (App Store Review Guideline 5.1.1(v): an app that
 * lets a person create an account must let them delete it from inside the app).
 *
 * Its own file rather than a section of auth.ts because it draws on both
 * halves — the credentials in auth.ts and the linked identities in
 * social-auth.ts, which already imports auth.ts. Putting it in either one
 * makes that import cycle.
 *
 * A DEACTIVATION rather than an erasure of the row: the profile is scrubbed,
 * but the email and phone stay ON the tombstone so the address remains claimed
 * and cannot be signed up again. Every sign-in path then meets the tombstone
 * and answers `This account has been deactivated`.
 *
 * Distinct from the admin close (`adminRoutes.members.deleteMember`) only in
 * who performs it and whether it can be undone — an admin close is reversible
 * via `restoreMember`, this one is not.
 *
 * ── no `password`, deliberately ─────────────────────────────────────────────
 *
 * This route once required re-authentication when the account had a password:
 * a bearer token proves somebody signed in, not that the person holding the
 * phone is the owner. That was dropped by product decision — the field was
 * unaskable for the Google and Apple accounts that are most of them, and every
 * other member action on this API is authorised by the token alone.
 *
 * The bearer token is now the ONLY thing standing in front of a permanent,
 * unrestorable close. Anything that raises the bar again — a re-auth step, a
 * grace period, a confirmation email — belongs here rather than in a caller.
 *
 * A client still sending `password` is not rejected: zod strips unknown keys,
 * so the old mobile build keeps working against a new server and simply has
 * its field ignored.
 */
export const DeleteAccountInput = z
    .object({
    /**
     * Kept on the tombstone, not on the member. Free text, never shown to
     * anyone but an admin reading why an account closed.
     */
    reason: z.string().max(500).optional(),
})
    /*
      An absent body is a VALID delete request, and has to be.
  
      `reason` is the only field and it is optional, so the natural call from a
      "Delete my account" button carries nothing at all — and a client that sends
      no body leaves `request.body` undefined, which an object schema rejects as
      "expected object, received undefined". Without this the endpoint 400s on
      its most common invocation, and the failure looks like a validation bug
      rather than a missing default.
    */
    .default({});
export const AccountDeleted = z.object({
    deleted: z.literal(true),
    deletedAt: z.coerce.date(),
    /**
     * Providers whose tokens were revoked WITH the provider, not merely
     * forgotten here.
     *
     * Apple requires this specifically: an app offering Sign in with Apple must
     * call `POST appleid.apple.com/auth/revoke` when the account goes, so the
     * member stops seeing ONOV in their Apple ID settings for an account that
     * no longer exists. Unlinking our own row is not the same act and does not
     * satisfy it.
     *
     * A provider missing from this list was unlinked but not revoked — the
     * deployment holds no revocation credentials for it, or the provider's
     * endpoint failed. Deletion is never held up by either: the account is gone
     * regardless, and a revocation that could block it would make an unreachable
     * third party able to refuse a member their erasure.
     */
    revoked: z.array(SocialProvider),
});
//# sourceMappingURL=account.js.map